import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, auth, money, fmtDate, exportExcel, printArea, PrintStyle, exportPdf } from '../shared';
import KpiCard from '../KpiCard';
import SimpleBarList from '../SimpleBarList';
import ReportActions from '../ReportActions';
import { SectionCard, EmptyState } from '../../../../components/dashboard/TodaySchedule';
import { getReportSettings, logExport } from '../../../../lib/reportStudioApi';
import { useClubIdentity } from '../../../../lib/useClubIdentity';
import ReportPrintFrame from '../../../../components/reportStudio/ReportPrintFrame';

const TYPE_LABELS = { coach: 'مدربون', medical: 'طبي', security: 'أمن', services: 'خدمات', admin: 'إداري', other: 'أخرى' };
const getUserName = () => { try { return JSON.parse(localStorage.getItem('user') || 'null')?.full_name; } catch { return null; } };

export default function HRTab({ filters, seasonId }) {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState(null); // null = غير متاح للصلاحية الحالية
  const [studio, setStudio] = useState(null);
  const identity = useClubIdentity();
  const printRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const qs = seasonId && seasonId !== 'all' ? `?season_id=${seasonId}` : '';
      const g = (url) => axios.get(`${API}${url}`, auth()).then((r) => r.data).catch(() => null);
      const [e, s, r, st] = await Promise.all([g(`/employees${qs}`), g('/employees/statistics/summary'), g('/performance-reviews'), getReportSettings('hr_report').catch(() => null)]);
      setEmployees(Array.isArray(e) ? e : []);
      setStats(s);
      setReviews(Array.isArray(r) ? r : null);
      setStudio(st);
      setLoading(false);
    })();
  }, [seasonId]);

  if (loading) {
    return <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  }

  const settings = studio?.effective || {};
  const content = settings.content || {};
  const templateUsed = studio?.use_custom ? 'تخصيص مستقل' : null;

  const newHires = employees.filter((e) => e.hire_date && e.hire_date.slice(0, 10) >= filters.date_from && e.hire_date.slice(0, 10) <= filters.date_to);
  const expiringSoon = employees.filter((e) => {
    if (!e.contract_end_date) return false;
    const diff = Math.ceil((new Date(e.contract_end_date) - new Date()) / 86400000);
    return diff >= 0 && diff <= 60;
  });
  const periodReviews = (reviews || []).filter((r) => r.review_date && r.review_date.slice(0, 10) >= filters.date_from && r.review_date.slice(0, 10) <= filters.date_to);
  const avgScore = periodReviews.length ? (periodReviews.reduce((s, r) => s + r.score, 0) / periodReviews.length).toFixed(1) : null;
  const typeItems = Object.entries(stats?.by_type || {}).map(([k, v]) => ({ label: TYPE_LABELS[k] || k, value: v }));
  const rows = employees.map((e) => ({ 'الاسم': e.full_name, 'النوع': TYPE_LABELS[e.employee_type] || e.employee_type, 'القسم': e.department || '—', 'تاريخ التعيين': e.hire_date, 'الحالة': e.is_active ? 'نشط' : 'معطل' }));

  const handlePdf = async () => {
    try {
      await exportPdf(printRef.current, 'تقرير-الموارد-البشرية', { orientation: settings.page?.orientation, paperSize: settings.page?.paper_size });
      logExport('hr_report', 'pdf', templateUsed, 'success', 'تقرير-الموارد-البشرية.pdf');
    } catch { logExport('hr_report', 'pdf', templateUsed, 'failed', null); }
  };
  const handleExcel = () => {
    exportExcel(rows, 'تقرير-الموارد-البشرية');
    logExport('hr_report', 'excel', templateUsed, 'success', 'تقرير-الموارد-البشرية.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="hr-tab">
      <PrintStyle />
      <div className="flex justify-end print:hidden">
        <ReportActions testId="hr-report" onExcel={handleExcel} onPdf={handlePdf} onPrint={printArea} disabled={rows.length === 0} />
      </div>
      <div className="print-area" ref={printRef}>
        <ReportPrintFrame settings={settings} identity={identity} reportTitle="تقرير الموارد البشرية" exportedByName={getUserName()}>
          <div className="space-y-4">
            {content.stat_cards !== false && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                <KpiCard icon="briefcase" label="إجمالي الموظفين" value={stats?.total_count ?? employees.length} testId="kpi-hr-total" />
                <KpiCard icon="users" label="النشطون" value={stats?.active_count ?? 0} testId="kpi-hr-active" />
                <KpiCard icon="plus" label="تعيينات الفترة" value={newHires.length} testId="kpi-hr-new" />
                <KpiCard icon="warning" label="عقود تنتهي قريباً" value={expiringSoon.length} accent={expiringSoon.length ? 'text-amber-600' : 'text-slate-900'} testId="kpi-hr-expiring" />
                <KpiCard icon="banknotes" label="الرواتب الشهرية" value={money(stats?.total_monthly_salaries)} testId="kpi-hr-salaries" />
              </div>
            )}

            {content.charts !== false && (
              <SectionCard title="توزيع الموظفين حسب النوع" icon="grid" testId="hr-distribution-card">
                {typeItems.length === 0 ? <EmptyState icon="briefcase" text="لا يوجد موظفون مسجلون" testId="hr-distribution-empty" /> : <SimpleBarList items={typeItems} testId="hr-distribution-bars" />}
              </SectionCard>
            )}

            {reviews !== null && content.tables !== false && (
              <SectionCard title="تقييمات الأداء خلال الفترة" icon="chart" testId="hr-reviews-card">
                {periodReviews.length === 0
                  ? <EmptyState icon="chart" text="لا توجد تقييمات أداء في هذه الفترة" testId="hr-reviews-empty" />
                  : <p className="text-sm text-slate-600">عدد التقييمات: <b>{periodReviews.length}</b> — المتوسط: <b data-testid="hr-avg-score">{avgScore}</b> / 5</p>}
              </SectionCard>
            )}

            {content.tables !== false && (
              <SectionCard title="عقود تنتهي خلال 60 يوماً" icon="warning" testId="hr-contracts-card">
                {expiringSoon.length === 0 ? <EmptyState icon="warning" text="لا توجد عقود تنتهي قريباً" testId="hr-contracts-empty" /> : (
                  <ul className="space-y-1 text-sm">{expiringSoon.map((e) => <li key={e.id} data-testid="hr-contract-row">{e.full_name} — {fmtDate(e.contract_end_date)}</li>)}</ul>
                )}
              </SectionCard>
            )}
          </div>
        </ReportPrintFrame>
      </div>
    </div>
  );
}
